// SPDX-License-Identifier: Apache-2.0

import i18n from "i18next";
import SignedState from "../api/SignedState";
import { BiCopy } from "react-icons/bi";
import { ChainNames, ethNetworkName } from "../constants/Constants";
import { ckbNetworkName } from "../constants/CKBNetwork";
import { AssetType } from "../api/types/ethereumAsset";

// If set to true, no notices are shown and the onSubmit function will be
// directly called.
let noticesDeactivated = false;
export const deactivateNotices = () => {
  noticesDeactivated = true;
};

export interface PerunNotice {
  title: string;
  content: JSX.Element;
  submitText?: string;
  onSubmit?: (msg?: SignedState) => void;
  cancelText?: string;
  onCancel?: () => void;
  autoClose?: boolean;
  // What deactivated (headless) mode auto-invokes. Defaults to "submit" —
  // right for the swap-flow confirmations headless runs exist to drive.
  // Recovery prompts (open channel / stranded funding found) must set
  // "cancel": their submit action abandons the session (e.g. shuts the client
  // down to navigate to FinishSwap), which would kill a headless run whenever
  // the backend restores a channel from a previous session.
  headlessAction?: "submit" | "cancel";
}

export const dispatchPerunNotice = (notice: PerunNotice) => {
  if (noticesDeactivated) {
    if (notice.headlessAction === "cancel") {
      notice.onCancel?.();
      return;
    }
    notice.onSubmit?.();
    return;
  }
  document.dispatchEvent(
    new CustomEvent<PerunNotice>("PerunNotice", {
      bubbles: true,
      detail: notice,
      cancelable: false,
    }),
  );
};

export const revertPerunNotice = () => {
  document.dispatchEvent(new CustomEvent<JSX.Element>("ClosePerunNotice"));
};

export const dispatchNavigateToFinish = () => {
  document.dispatchEvent(new CustomEvent("NavigateToFinish"));
};

export const swapRateInfoNotice = (
  fromTxFee: string,
  toTxFee: string,
  exchangeRate: number,
  fromChainID: bigint,
  toChainID: bigint,
  fromAssetCode: string,
  toAssetCode: string,
): PerunNotice => {
  const content = (
    <>
      <b>{i18n.t("noticeExchangeRate.intro")}</b>
      <b>
        {i18n.t("noticeExchangeRate.youPay", { fromAssetCode: fromAssetCode })}
      </b>
      <b>
        {i18n.t("noticeExchangeRate.youReceive", { toAssetCode: toAssetCode })}
      </b>
      <br />
      <p>
        {i18n.t("noticeExchangeRate.rate")} = {exchangeRate}
      </p>
      <p>
        {i18n.t("noticeExchangeRate.fromTxFee", {
          fromChainName: ChainNames.get(fromChainID),
          assetName: fromAssetCode,
        })}
        {"\n = "}
        {fromTxFee} {fromAssetCode}
      </p>
      <p>
        {i18n.t("noticeExchangeRate.toTxFee", {
          toChainName: ChainNames.get(toChainID),
          assetName: toAssetCode,
        })}
        {"\n = "}
        {toTxFee} {toAssetCode}
      </p>
    </>
  );
  return {
    title: i18n.t("noticeExchangeRate.title"),
    content: content,
    submitText: `${i18n.t("okay")}!`,
    onSubmit: () => {},
  };
};

// The amounts are recomputed with a fresh quote when Swap is pressed. If the
// payout dropped meaningfully in the meantime — the gas price rose — the user
// is shown the new amounts instead of having the swap proposed at a price they
// never saw.
export const quoteChangedNotice = (): PerunNotice => ({
  title: i18n.t("quoteChangedTitle"),
  content: <p>{i18n.t("quoteChanged")}</p>,
  submitText: `${i18n.t("okay")}!`,
  onSubmit: () => {},
});

export const initiateSwapNotice = (
  fromAssetName: string,
  assetType: AssetType,
  toAssetType: AssetType,
  onSubmit: () => void,
  onCancel: () => void,
): PerunNotice => {
  // Which requests the wallet will actually raise depends on the direction:
  // paying on Ethereum means a deposit (a token also an approval), and any swap
  // with CKB opens and later closes the channel on Nervos, each with a
  // signature. Receiving on Ethereum raises nothing: the hub concludes and
  // withdraws there. The list used to name only the deposit, so the Nervos
  // signatures arrived unannounced.
  const paysOnEthereum = assetType === "ETH" || assetType === "ERC20";
  const involvesCKB = assetType === "CKB" || toAssetType === "CKB";
  const content = (
    <>
      <b>{i18n.t("noticeInitiateSwap.part1a")}</b>
      <p>{i18n.t("noticeInitiateSwap.part1")}:</p>
      <ol>
        {(assetType === "native" || assetType === "credit") && (
          <li>{i18n.t("noticeInitiateSwap.part2")}</li>
        )}
        {assetType === "ERC20" && (
          <li>
            {i18n.t("noticeInitiateSwap.part3ERC20", {
              assetName: fromAssetName,
            })}
          </li>
        )}
        {paysOnEthereum && (
          <li>
            {i18n.t("noticeInitiateSwap.part4", {
              assetName: fromAssetName,
            })}
          </li>
        )}
        {involvesCKB && <li>{i18n.t("noticeInitiateSwap.nervosOpen")}</li>}
        {involvesCKB && <li>{i18n.t("noticeInitiateSwap.nervosClose")}</li>}
      </ol>
      <b>{i18n.t("noticeInitiateSwap.part5")}.</b>
    </>
  );

  return {
    title: i18n.t("metaMaskNotice"),
    content: content,
    submitText: `${i18n.t("okay")}!`,
    onSubmit: onSubmit,
    cancelText: `${i18n.t("cancel")}`,
    onCancel: onCancel,
  };
};

export const performingSwapNotice = (signedState?: string): PerunNotice => {
  return {
    title: i18n.t("SwapUpdate"),
    content: (
      <>
        <p>{i18n.t("noticeSwapUpdate")}.</p>
        {signedState && (
          <>
            <p>{i18n.t("saveState_funded")}</p>
            <textarea
              readOnly
              className="scrollabletextbox"
              defaultValue={signedState}
              id="note"
            ></textarea>
            <div
              className="copy-button"
              id="copyButton"
              onClick={() => copyToClipboard(signedState)}
            >
              <BiCopy size={20} />
            </div>
          </>
        )}
      </>
    ),
    autoClose: true,
  };
};

export const finalizingSwapNotice = (signedState?: string): PerunNotice => {
  const content = (
    <>
      <b>{i18n.t("noticeSwapClosing.part1")}.</b>
      <p> {i18n.t("noticeSwapClosing.part2")}</p>
      {signedState && (
        <>
          <p>{i18n.t("saveState_concluded")}</p>
          <textarea
            readOnly
            className="scrollabletextbox"
            defaultValue={signedState}
            id="note"
          ></textarea>
          <div
            className="copy-button"
            id="copyButton"
            onClick={() => copyToClipboard(signedState)}
          >
            <BiCopy size={20} />
          </div>
        </>
      )}
    </>
  );
  return {
    title: i18n.t("SwapFinalize"),
    content: content,
    autoClose: true,
  };
};

export const recoveredFundsNotice = (): PerunNotice => {
  const content = (
    <>
      <b> {i18n.t("fundsRecovered")}</b>
    </>
  );
  return {
    title: i18n.t("success"),
    content: content,
    autoClose: true,
  };
};

export const recoverFundingRunningNotice = (): PerunNotice => {
  const content = (
    <>
      <p>{i18n.t("recoverFundingRunning")}</p>
    </>
  );
  return {
    title: i18n.t("information"),
    content: content,
  };
};

export const recoverFundingFailedNotice = (error: unknown): PerunNotice => {
  const content = (
    <>
      <p>{i18n.t("recoverFundingFailed")}</p>
      <p className="channel-id-display">
        <code>{String(error)}</code>
      </p>
    </>
  );
  return {
    title: i18n.t("error"),
    content: content,
  };
};

export const openChannelFoundNotice = (
  signedStates: string[],
  onClose: (index: number) => void,
): PerunNotice => {
  console.log("Open channel found notice, signedStates: ", signedStates);

  const preview = (s: string) =>
    s.slice(0, 80) + (s.length > 80 ? "..." : "");

  if (signedStates.length === 1) {
    const state = signedStates[0];

    const content = (
      <>
        <p>{i18n.t("noticeOpenChannelFound.part1")}</p>
        <p>{i18n.t("noticeOpenChannelFound.part2")}</p>
        <textarea
          readOnly
          className="scrollabletextbox"
          defaultValue={state}
        />
        <div
          className="copy-button"
          onClick={() => copyToClipboard(state)}
        >
          <BiCopy size={20} />
        </div>
      </>
    );

    return {
      title: i18n.t("openChannelFound"),
      content,
      submitText: i18n.t("closeChannel"),
      onSubmit: () => onClose(0),
      cancelText: i18n.t("cancel"),
      onCancel: () => {},
      // Recovery prompt: submit shuts the client down (FinishSwap flow), so a
      // headless run must decline instead of dying on a restored channel.
      headlessAction: "cancel",
    };
  }
  const content = (
    <form id="signedstate-form">
      <p>{i18n.t("noticeOpenChannelFound.part1")}</p>
      <p>{i18n.t("noticeOpenChannelFound.part2")}</p>

      <div className="signedstate-list">
        {signedStates.map((state, i) => (
          <label key={i} className="signedstate-item">
            <input
              type="radio"
              name="signedstate"
              value={i}
              defaultChecked={i === 0}
            />

            <div className="signedstate-preview">
              {preview(state)}
            </div>

            <div
              className="copy-button"
              onClick={(e) => {
                e.preventDefault(); // don’t toggle radio accidentally
                e.stopPropagation();
                copyToClipboard(state);
              }}
            >
              <BiCopy size={20} />
            </div>
          </label>
        ))}
      </div>
    </form>
  );

  return {
    title: i18n.t("openChannelFound"),
    content,
    submitText: i18n.t("closeChannel"),
    onSubmit: () => {
      const form = document.getElementById(
        "signedstate-form",
      ) as HTMLFormElement | null;

      if (!form) {
        onClose(0);
        return;
      }

      const formData = new FormData(form);
      const selected = formData.get("signedstate");

      const idx = selected ? parseInt(selected as string, 10) : 0;

      onClose(idx);
    },
    cancelText: i18n.t("cancel"),
    onCancel: () => {},
    // Recovery prompt — decline in headless mode (submit shuts the client down).
    headlessAction: "cancel",
  };
};

export const fundingChannelFoundNotice = (
  channelIDs: string[],
  onRecover: (index: number) => void,
): PerunNotice => {
  console.log("Funding channel found notice, channelIDs: ", channelIDs);

  const preview = (s: string) =>
    s.slice(0, 20) + (s.length > 20 ? "..." : "");

  if (channelIDs.length === 1) {
    const chID = channelIDs[0];

    const content = (
      <>
        <p>{i18n.t("noticeFundingChannelFound.part1")}</p>
        <p>{i18n.t("noticeFundingChannelFound.part2")}</p>
        <p className="channel-id-display"><code>{chID}</code></p>
      </>
    );

    return {
      title: i18n.t("fundingChannelFound"),
      content,
      submitText: i18n.t("recoverFunding"),
      onSubmit: () => onRecover(0),
      cancelText: i18n.t("cancel"),
      onCancel: () => {},
      // Recovery prompt — decline in headless mode (see openChannelFoundNotice).
      headlessAction: "cancel",
    };
  }

  const content = (
    <form id="fundingchannel-form">
      <p>{i18n.t("noticeFundingChannelFound.part1")}</p>
      <p>{i18n.t("noticeFundingChannelFound.part2")}</p>

      <div className="signedstate-list">
        {channelIDs.map((chID, i) => (
          <label key={i} className="signedstate-item">
            <input
              type="radio"
              name="fundingchannel"
              value={i}
              defaultChecked={i === 0}
            />
            <div className="signedstate-preview">
              {preview(chID)}
            </div>
          </label>
        ))}
      </div>
    </form>
  );

  return {
    title: i18n.t("fundingChannelFound"),
    content,
    submitText: i18n.t("recoverFunding"),
    onSubmit: () => {
      const form = document.getElementById(
        "fundingchannel-form",
      ) as HTMLFormElement | null;

      if (!form) {
        onRecover(0);
        return;
      }

      const formData = new FormData(form);
      const selected = formData.get("fundingchannel");
      const idx = selected ? parseInt(selected as string, 10) : 0;
      onRecover(idx);
    },
    cancelText: i18n.t("cancel"),
    onCancel: () => {},
    // Recovery prompt — decline in headless mode (see openChannelFoundNotice).
    headlessAction: "cancel",
  };
};

function copyToClipboard(signedState: string): void {
  navigator.clipboard.writeText(signedState).then(
    () => {
      console.log("Content copied to clipboard");

      // Resolved - text copied to clipboard successfully
    },
    () => {
      console.log("Failed to copy");
      // Rejected - text failed to copy to the clipboard
    },
  );
}

/**
 * Set-up steps for the swap app. Rendered both as the first-visit notice and
 * inside the help menu's "Instructions" entry — one body so the two cannot
 * drift apart. Network names are derived from the build's configuration
 * (never hardcoded): the deployed mainnet page once told users to switch
 * MetaMask to Sepolia and fund testnet accounts.
 */
export function InstructionsBody() {
  return (
    <>
      <p>
        Before you begin using <strong>Perun-X</strong>, we would like to
        provide you with some instructions on how to set up and use this web
        app:
      </p>
      <ol>
        <li>
          <strong>Install MetaMask:</strong>
          <p>
            Ensure you have{" "}
            <a
              href="https://metamask.io/"
              target="_blank"
              rel="noopener noreferrer"
            >
              MetaMask
            </a>{" "}
            installed. It handles the Ethereum side of a swap.
          </p>
        </li>
        <li>
          <strong>Configure MetaMask:</strong>
          <ul>
            <li>
              Switch MetaMask to the <em>"{ethNetworkName}"</em> network.
            </li>
          </ul>
        </li>
        <li>
          <strong>Connect the Nervos side:</strong>
          <ul>
            <li>
              The <em>CKB connect</em> button opens the Nervos wallet dialog.
              Choose MetaMask there as well: your CKB address is derived from
              the same Ethereum account, so native CKB wallets such as JoyID
              cannot sign for this app.
            </li>
            <li>After connecting, the same button shows your CKB address.</li>
          </ul>
        </li>
        <li>
          <strong>Fund your accounts:</strong>
          <p>
            Swaps settle on {ethNetworkName} and Nervos {ckbNetworkName}, so
            your wallets need funds on both chains.
          </p>
        </li>
        <li>
          <strong>Reload the Site:</strong>
          <p>
            After installing a wallet, you may need to reload the webpage.
          </p>
        </li>
        <li>
          <strong>Start swapping:</strong>
          <ul>
            <li>
              Once both sides are connected, click <strong>"Onboard"</strong>,
              pick the assets and the amount, and confirm.
            </li>
            <li>
              MetaMask will ask you to sign one message and then to confirm
              three to four transactions, one per on-chain step (funding,
              settlement, close). Approve each one; a rejected request aborts
              the swap.
            </li>
            <li>
              Keep the same MetaMask account selected until the swap shows{" "}
              <strong>"Swap was successful!"</strong>. Switching accounts
              mid-swap makes the CKB signature fail and can leave the channel
              open with your deposit in it.
            </li>
          </ul>
        </li>
      </ol>
      <p>
        We welcome your feedback, so please feel free to share your thoughts
        with us.
      </p>
    </>
  );
}

export const Instructions = () => {
  const content = (
    <div
      style={{
        fontFamily: "Arial, sans-serif",
        lineHeight: "1.6",
        padding: "20px",
      }}
    >
      <InstructionsBody />
    </div>
  );
  return {
    title: "Instructions",
    content: content,
    autoClose: false,
  };
};
